export type ScenarioId = "1" | "2" | "3" | "4" | "5";

export interface Scenario {
  id: ScenarioId;
  name: string;
  description: string;
  tp_definition: string;
  aq_definition: string;
  comparison_logic: string;
  outputs: string;
  intent: string;
}

export const SCENARIOS: Record<ScenarioId, Scenario> = {
  "1": {
    id: "1",
    name: "Self Reflection",
    description: "Personal insight with no external comparison",
    tp_definition: "Self-defined",
    aq_definition: "Self",
    comparison_logic: "Self vs Self only (no external comparisons)",
    outputs: "Personal maturity profile, Reflection insights",
    intent: "Self-initiated by participant",
  },
  "2": {
    id: "2",
    name: "Candidate Assessment",
    description: "Client evaluates a specific individual",
    tp_definition: "Self-defined",
    aq_definition: "Self",
    comparison_logic: "No cross-candidate comparison",
    outputs: "Candidate assessment profile",
    intent: "Client assigns assessment to candidate",
  },
  "3": {
    id: "3",
    name: "Candidate Comparison",
    description: "Compare multiple candidates or team members",
    tp_definition: "Self-defined",
    aq_definition: "Self",
    comparison_logic: "TP compared across candidates; AQ compared across candidates",
    outputs: "Comparative pattern views",
    intent: "Client initiates multi-candidate assessment",
  },
  "4": {
    id: "4",
    name: "Best Fit Identification",
    description: "Identify best fit for a specific role",
    tp_definition: "Client-defined (Role TP)",
    aq_definition: "Self",
    comparison_logic: "Candidate AQ vs role-defined TP; Candidate AQ vs Candidate AQ",
    outputs: "Role-fit indicators, Alignment gaps",
    intent: "Client defines role requirements and initiates assessment",
  },
  "5": {
    id: "5",
    name: "Succession Planning",
    description: "Assess readiness for next-level roles",
    tp_definition: "Client-defined",
    aq_definition: "Self",
    comparison_logic: "Candidate AQ vs client baseline AQ; Optional comparison to required TP",
    outputs: "Readiness assessment, Development gaps",
    intent: "Client initiates succession assessment",
  },
};

export const SCENARIO_LIST = Object.values(SCENARIOS);

/**
 * Scenarios where the Client Manager defines the Time Prioritization matrix
 * (Role TP), so the candidate has nothing to fill out for TP and just waits
 * for the company to finish that step before the assessment opens.
 *
 * Mirrors the backend's ``_CLIENT_TP_SCENARIOS`` set in
 * ``xyden.core.assessment.question_queue``.
 */
export const CLIENT_TP_SCENARIO_IDS: ReadonlySet<string> = new Set(["4", "5"]);

export function isClientTPScenario(
  scenario: { id?: string | number; skip_tp?: boolean } | string | number | null | undefined
): boolean {
  if (scenario == null) return false;
  if (typeof scenario === "object") {
    if (scenario.skip_tp === true) return true;
    return scenario.id != null && CLIENT_TP_SCENARIO_IDS.has(String(scenario.id));
  }
  return CLIENT_TP_SCENARIO_IDS.has(String(scenario));
}
